import { b2cPath, type B2CPlatform } from '@/utils/paths';

/** The B2C subset of the platforms, owned by `paths.ts`. Aliased rather than
 * re-listed so the two can never disagree about who has founders. */
export type EntrepreneurPlatform = B2CPlatform;

/**
 * `b2cPath` rather than `ePath`: these links target a NAMED platform's portal,
 * and every caller of `platformRedirectPath` may be pointing at the platform
 * the user is NOT on (the portal switcher). `ePath` resolves from where you
 * are; it would keep them on the current host. Same relative path otherwise.
 */
const portalPath = (platform: EntrepreneurPlatform) => (path: string) =>
  b2cPath(platform, path);

export interface EntrepreneurPlatformAccess {
  platform: EntrepreneurPlatform;
  status: string;
  organization_id: string | null;
  activated_at: string | null;
  created_at: string | null;
  is_active: boolean;
  is_disabled: boolean;
  redirect_path: string | null;
}

interface PlatformFrontendConfig {
  label: string;
  toPath: (path: string) => string;
  dashboard: string;
  onboarding: string | null;
  verifyEmail: string | null;
}

/**
 * Frontend mirror of the backend PLATFORM_CONFIG
 * (src/services/entrepreneur_platform/config.py). FoundersHub and GAIA share
 * the same redirect/gating logic — the only differences are these config
 * entries. Keep both sides in sync when adding a platform.
 *
 * The B2C portal is a section INSIDE each platform (`/entrepreneur/*`), never
 * its own host, so every platform resolves through the same `b2cPath`.
 */
export const PLATFORM_FRONTEND_CONFIG: Record<EntrepreneurPlatform, PlatformFrontendConfig> = {
  foundershub: {
    label: 'Founders Hub',
    toPath: portalPath('foundershub'),
    dashboard: '/dashboard',
    onboarding: '/onboarding',
    verifyEmail: '/verify-email',
  },
  gaia: {
    label: 'Gaia',
    toPath: portalPath('gaia'),
    dashboard: '/my-projects',
    onboarding: null,
    verifyEmail: '/verify-email',
  },
};

const GAIA_PENDING_EMAIL_STATUS = 'PENDING_EMAIL_VERIFICATION';

/** Where a platform's founders' portal lands you — the one place that knows
 * gaia's portal opens on its projects and FoundersHub's on its dashboard. */
export function platformPortalHome(platform: EntrepreneurPlatform): string {
  const cfg = PLATFORM_FRONTEND_CONFIG[platform];
  return cfg.toPath(cfg.dashboard);
}

/** Relative portal path the client should land on for one access. */
export function platformRedirectSuffix(access: EntrepreneurPlatformAccess): string {
  const cfg = PLATFORM_FRONTEND_CONFIG[access.platform];
  if (access.is_active) return cfg.dashboard;
  if (access.is_disabled) return cfg.dashboard;
  if (cfg.verifyEmail && access.status === GAIA_PENDING_EMAIL_STATUS) {
    return cfg.verifyEmail;
  }
  return cfg.onboarding ?? cfg.dashboard;
}

/**
 * Full app path the client should land on for one access, resolved into the
 * access's own platform.
 *
 * The backend already resolved the suffix from PLATFORM_CONFIG and sends it as
 * `redirect_path`, so that is the source of truth — otherwise the two tables
 * are free to drift and nothing catches it. `platformRedirectSuffix` stays as
 * the fallback for payloads that predate the field. Only the platform prefix
 * (or host) is a frontend concern.
 */
export function platformRedirectPath(access: EntrepreneurPlatformAccess): string {
  const cfg = PLATFORM_FRONTEND_CONFIG[access.platform];
  return cfg.toPath(access.redirect_path ?? platformRedirectSuffix(access));
}

/**
 * Resolve where to send the client given all their accesses and an optional
 * requested platform (e.g. ?platform=gaia on the login origin).
 *
 * - requested platform present → its target path
 * - otherwise first ACTIVE access → its dashboard
 * - otherwise first access → its pending target (onboarding / verify email)
 */
export function resolvePlatformTarget(
  accesses: EntrepreneurPlatformAccess[],
  requestedPlatform?: EntrepreneurPlatform | null,
): string | null {
  if (!accesses.length) return null;

  if (requestedPlatform) {
    const requested = accesses.find((a) => a.platform === requestedPlatform);
    if (requested) return platformRedirectPath(requested);
  }

  const active = accesses.find((a) => a.is_active);
  if (active) return platformRedirectPath(active);

  return platformRedirectPath(accesses[0]);
}
